import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertCircle, Lock } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

export default function ConciergeLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);

    // If already authenticated, bounce to dashboard.
    useEffect(() => {
        let cancelled = false;
        adminApi
            .me()
            .then(() => {
                if (!cancelled) navigate("/concierge", { replace: true });
            })
            .catch(() => {})
            .finally(() => !cancelled && setChecking(false));
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            // Trim the password to defend against autofill managers that
            // append a trailing whitespace on some browsers (observed on
            // Chrome + 1Password combo). Email is already trimmed above.
            await adminApi.login(email.trim(), password.trim());
            // Hard reload — forces the browser to include the freshly-set
            // `act_admin` cookie on the very next request. A client-side
            // navigate() is susceptible to a cookie-not-yet-available race
            // in some browsers (Safari + strict Firefox profiles), where
            // the dashboard's /me check fires before the cookie is visible
            // to XHR, returns 401, and the route guard bounces straight
            // back to /concierge/login with cleared form state.
            window.location.assign("/concierge");
        } catch (err) {
            if (err.status === 429) {
                setError(err.message || "Too many failed attempts. Try again later.");
            } else {
                setError("Credentials not recognized.");
            }
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[#0B0B0C] text-[#F3EFE7] flex flex-col"
            data-testid="concierge-login-page"
        >
            <Helmet>
                <title>Concierge Access — ACT</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            {/* Minimal header — wordmark only */}
            <header className="border-b border-[rgba(201,168,106,0.12)]">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
                    <Link
                        to="/"
                        className="font-serif text-[#C9A86A] text-xl tracking-[0.2em] hover:text-[#D9BC80] transition-colors duration-300"
                        data-testid="concierge-login-home-link"
                    >
                        ACT
                    </Link>
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A]">
                        Concierge Access
                    </span>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-md">
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        className="relative border border-[rgba(201,168,106,0.3)] bg-[#15161A] p-10 md:p-12 shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
                        data-testid="concierge-login-form"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <span className="inline-flex items-center justify-center w-9 h-9 border border-[#C9A86A]/50 text-[#C9A86A]">
                                <Lock size={15} strokeWidth={1.4} />
                            </span>
                            <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A]">
                                Private
                            </span>
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl text-[#F3EFE7] leading-[1.1] mb-8">
                            Concierge Access
                        </h1>

                        <div className="space-y-6">
                            <div>
                                <label
                                    htmlFor="admin-email"
                                    className="block text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A] mb-3"
                                >
                                    Email
                                </label>
                                <input
                                    id="admin-email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full bg-transparent border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300"
                                    data-testid="concierge-login-email"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="admin-password"
                                    className="block text-[10.5px] tracking-[0.32em] uppercase text-[#9A958A] mb-3"
                                >
                                    Password
                                </label>
                                <input
                                    id="admin-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full bg-transparent border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-base py-2.5 px-1 transition-colors duration-300"
                                    data-testid="concierge-login-password"
                                />
                            </div>

                            {error && (
                                <div
                                    role="alert"
                                    className="flex items-start gap-2 border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A] leading-[1.7]"
                                    data-testid="concierge-login-error"
                                >
                                    <AlertCircle
                                        size={14}
                                        strokeWidth={1.6}
                                        className="mt-[2px] shrink-0"
                                    />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || checking}
                                className="btn-gold w-full disabled:opacity-70 disabled:cursor-wait"
                                data-testid="concierge-login-submit"
                            >
                                {loading ? (
                                    <>
                                        <span className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                        Signing in
                                    </>
                                ) : (
                                    "Sign In"
                                )}
                            </button>
                        </div>
                    </form>

                    <p className="mt-8 text-center text-[12px] text-[#6e6a62] leading-[1.8] max-w-sm mx-auto">
                        This area is restricted. Inquiries about access should
                        be made via the{" "}
                        <Link
                            to="/contact"
                            className="text-[#9A958A] hover:text-[#C9A86A] transition-colors duration-300 underline underline-offset-4 decoration-[rgba(201,168,106,0.3)]"
                        >
                            public contact form
                        </Link>
                        .
                    </p>
                </div>
            </main>
        </div>
    );
}
